// src/pages/admin/employmentInquiry/EmploymentInquiryDetail.js
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import AdminLayout from "../../../layouts/admin/AdminLayout";
import api from "../../../api/axios";
import { NATIONALITY_OPTIONS } from "../../../contants/nationalities";
import { useTranslation } from "react-i18next";

export default function EmploymentInquiryDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();

  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [adminMemo, setAdminMemo] = useState("");
  const [savingMemo, setSavingMemo] = useState(false);
  const [infoMsg, setInfoMsg] = useState("");

  const fmtDateTime = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    const hh = String(dt.getHours()).padStart(2, "0");
    const mi = String(dt.getMinutes()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd} ${hh}:${mi}`;
  };

  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    const yyyy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yyyy}.${mm}.${dd}`;
  };

  const genderLabel =
    item?.gender === "M"
      ? t("contact.gender.male")
      : item?.gender === "F"
        ? t("contact.gender.female")
        : item?.gender || "-";

  const getNationalityLabel = (value) => {
    if (!value) return "-";
    const found = NATIONALITY_OPTIONS.find((n) => n.code === value);
    // 코드에 매칭되면 라벨, 아니면 그대로(기타 입력값)
    return found ? found.label : value;
  };

  const nationalityLabel = getNationalityLabel(item?.nationality);

  const load = async () => {
    setLoading(true);
    setError("");
    setInfoMsg("");
    try {
      // ✅ 상세: GET /api/employment/inquiry/{id}
      const res = await api.get(`/api/employment/inquiry/${id}`);
      const code = res.data?.resultCode;

      if (code !== "200" && code !== 200) {
        setError(
          res.data?.resultMessage || "지원서 상세를 불러오지 못했습니다.",
        );
        setItem(null);
        return;
      }

      const data = res.data?.data ?? null;
      setItem(data);
      setAdminMemo(data?.adminMemo ?? "");
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage ||
          "지원서 상세 조회 중 오류가 발생했습니다.",
      );
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const parsed = useMemo(() => {
    const safeParse = (s) => {
      try {
        return s ? JSON.parse(s) : [];
      } catch {
        return [];
      }
    };

    return {
      education: safeParse(item?.educationJson),
      langTests: safeParse(item?.langTestsJson),
      licenses: safeParse(item?.licensesJson),
    };
  }, [item]);

  // ✅ 관리자 메모 저장: PATCH /api/employment/inquiry/{id}/memo
  const onSaveMemo = async () => {
    setSavingMemo(true);
    setError("");
    setInfoMsg("");
    try {
      const res = await api.patch(`/api/employment/inquiry/${id}/memo`, {
        adminMemo: adminMemo ?? "",
      });
      const code = res.data?.resultCode;

      if (code === "200" || code === 200) {
        setInfoMsg("메모가 저장되었습니다.");
        const updated = res.data?.data;
        if (updated) {
          setItem(updated);
          setAdminMemo(updated?.adminMemo ?? "");
        } else {
          // 응답에 data가 없으면 재조회
          await load();
        }
      } else {
        setError(res.data?.resultMessage || "메모 저장에 실패했습니다.");
      }
    } catch (e) {
      console.error(e);
      setError(
        e.response?.data?.resultMessage || "메모 저장 중 오류가 발생했습니다.",
      );
    } finally {
      setSavingMemo(false);
    }
  };

  const yesNo = (b) => (b ? "예" : "아니오");

  return (
    <AdminLayout>
      {/* 헤더 */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-blue-900 md:text-3xl">
            채용공고 지원서 상세
          </h1>
          <p className="mt-1 text-xs text-slate-500">ID: {id}</p>
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => nav("/admin/employment/inquiry/list")}
            className="rounded-xl border bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            목록으로
          </button>
        </div>
      </div>

      <div className="rounded-2xl border bg-white p-6 shadow-sm md:p-8">
        {/* 상태 */}
        {loading && (
          <div className="py-10 text-center text-slate-500">
            불러오는 중입니다...
          </div>
        )}

        {!loading && error && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && infoMsg && (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
            {infoMsg}
          </div>
        )}

        {!loading && !error && !item && (
          <div className="py-10 text-center text-slate-500">
            해당 지원서를 찾을 수 없습니다.
          </div>
        )}

        {!loading && !error && item && (
          <div className="space-y-6">
            {/* 지원 대상 공고 */}
            <section className="rounded-2xl border bg-slate-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-semibold text-slate-800">
                    지원 대상 채용공고
                  </div>
                  <div className="mt-2 text-base font-extrabold text-slate-900">
                    {item.employmentTitle || "-"}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    employmentId:{" "}
                    <span className="font-semibold text-slate-700">
                      {item.employmentId ?? "-"}
                    </span>
                  </div>
                </div>

                {item.employmentId ? (
                  <Link
                    to={`/admin/employment/${item.employmentId}`}
                    className="inline-flex items-center rounded-xl border bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    공고 상세로
                  </Link>
                ) : null}
              </div>
            </section>

            {/* 메타 */}
            <section className="grid grid-cols-1 gap-3 rounded-2xl border p-4 md:grid-cols-3">
              <Meta label="접수일" value={fmtDateTime(item.createdAt)} />
              <Meta label="수정일" value={fmtDateTime(item.updatedAt)} />
              <Meta label="한국 내 거주" value={yesNo(!!item.livingInKorea)} />
            </section>

            {/* 기본 정보 */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">
                기본 정보
              </h2>

              <br></br>

              {item.idPhotoUrl && (
                <div className="flex justify-start">
                  <img
                    src={item.idPhotoUrl}
                    crossOrigin="anonymous"
                    alt="증명사진"
                    className="h-32 w-28 rounded-lg border object-cover"
                  />
                </div>
              )}

              <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                <Meta label="이름" value={item.name} />
                <Meta label="성별" value={genderLabel} />
                <Meta
                  label="생년월일"
                  value={item.birthDate ? fmtDate(item.birthDate) : ""}
                />
                <Meta label="국적" value={nationalityLabel} />
                <Meta label="비자 타입" value={item.visaType || "-"} />
                <Meta label="이메일" value={item.email} />
                <Meta label="전화" value={item.phone} />
                <Meta
                  label="주소"
                  value={item.address}
                  className="md:col-span-2"
                />
              </div>
            </section>

            {/* 경력 요약 */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">
                경력 요약
              </h2>
              <pre className="mt-3 whitespace-pre-wrap rounded-xl border bg-slate-50 p-3 text-sm text-slate-800">
                {item.experience || "-"}
              </pre>
            </section>

            {/* 학력 (JSON) */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">학력</h2>

              {parsed.education?.length ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b bg-slate-50 text-xs font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2">학위/전공</th>
                        <th className="px-3 py-2">학교</th>
                        <th className="px-3 py-2">졸업</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.education.map((r, i) => (
                        <tr
                          key={i}
                          className="border-b last:border-b-0 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2">{r.degree}</td>
                          <td className="px-3 py-2">{r.institution}</td>
                          <td className="px-3 py-2">{r.gradDate}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-3 text-sm text-slate-500">데이터 없음</div>
              )}
            </section>

            {/* 어학시험 (JSON) */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">
                어학시험
              </h2>

              {parsed.langTests?.length ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b bg-slate-50 text-xs font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2">언어</th>
                        <th className="px-3 py-2">시험</th>
                        <th className="px-3 py-2">점수</th>
                        <th className="px-3 py-2">취득</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.langTests.map((r, i) => (
                        <tr
                          key={i}
                          className="border-b last:border-b-0 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2">{r.lang}</td>
                          <td className="px-3 py-2">{r.exam}</td>
                          <td className="px-3 py-2">{r.score}</td>
                          <td className="px-3 py-2">{r.acquired}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-3 text-sm text-slate-500">데이터 없음</div>
              )}
            </section>

            {/* 자격/면허 (JSON) */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">
                자격/면허
              </h2>

              {parsed.licenses?.length ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="min-w-full text-left text-sm">
                    <thead className="border-b bg-slate-50 text-xs font-semibold text-slate-600">
                      <tr>
                        <th className="px-3 py-2">자격명</th>
                        <th className="px-3 py-2">발급기관</th>
                        <th className="px-3 py-2">취득</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parsed.licenses.map((r, i) => (
                        <tr
                          key={i}
                          className="border-b last:border-b-0 hover:bg-slate-50"
                        >
                          <td className="px-3 py-2">{r.name}</td>
                          <td className="px-3 py-2">{r.issuer}</td>
                          <td className="px-3 py-2">{r.acquired}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-3 text-sm text-slate-500">데이터 없음</div>
              )}
            </section>

            {/* 첨부파일 */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">
                첨부파일
              </h2>

              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {/* <FileBox label="증명사진" url={item.idPhotoUrl} required /> */}
                <FileBox label="이력서" url={item.resumeUrl} />
              </div>
            </section>

            {/* 사전 확인 */}
            <section className="rounded-2xl border p-4">
              <h2 className="text-base font-extrabold text-slate-900">
                사전 확인
              </h2>

              <div className="mt-3 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <Check label="elig1" value={item.elig1} />
                <Check label="elig2" value={item.elig2} />
                <Check label="elig3" value={item.elig3} />
                <Check label="elig4" value={item.elig4} />
                <Check label="elig5" value={item.elig5} />
                <Check label="elig6" value={item.elig6} />
              </div>

              <div className="mt-3 grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                <Check label="개인정보 동의(agree)" value={item.agree} />
                <Check
                  label="사실 확인(confirmTruth)"
                  value={item.confirmTruth}
                />
              </div>
            </section>

            {/* 관리자 메모 */}
            <section className="rounded-2xl border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-extrabold text-slate-900">
                  관리자 메모
                </h2>
                <button
                  type="button"
                  onClick={onSaveMemo}
                  disabled={savingMemo}
                  className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
                >
                  {savingMemo ? "저장 중..." : "메모 저장"}
                </button>
              </div>

              <textarea
                value={adminMemo}
                onChange={(e) => setAdminMemo(e.target.value)}
                rows={6}
                className="mt-3 w-full rounded-xl border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-200"
                placeholder="관리자 메모를 입력해 주세요."
              />
            </section>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function Meta({ label, value, className = "" }) {
  return (
    <div className={`rounded-xl border bg-white p-3 ${className}`}>
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-medium text-slate-900 break-words">
        {value || "-"}
      </div>
    </div>
  );
}

function Check({ label, value }) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-bold text-slate-900">
        {value ? "YES" : "NO"}
      </div>
    </div>
  );
}

function FileBox({ label, url, required }) {
  return (
    <div className="rounded-xl border bg-white p-3">
      <div className="text-xs font-semibold text-slate-500">
        {label}
        {required ? <span className="text-rose-600"> *</span> : null}
      </div>

      <div className="mt-2 text-sm">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center rounded-lg border bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            열기
          </a>
        ) : (
          <span className="text-slate-500">없음</span>
        )}
      </div>

      {url ? (
        <div className="mt-2 break-all text-xs text-slate-500">{url}</div>
      ) : null}
    </div>
  );
}
